import { For, Show, createSignal } from 'solid-js'
import { A, useLocation, useNavigate } from '@solidjs/router'
import { userStore } from '../../store'
import { navSections } from './navData'
import { IconChevRight, IconTwitter, IconInstagram, IconFacebook } from './Icons'
import type { NavItem } from './navData'
import { TokenCoin } from './TokenCoin'

export function Sidebar(props: { collapsed: boolean; onToggle: () => void }) {
  const location = useLocation()
  const navigate = useNavigate()
  const user = () => userStore()
  const loggedIn = () => user().loggedIn
  const tokens = () => user().profile?.totalAvailableTokens ?? 0

  const handleWalletClick = () => {
    loggedIn() ? userStore.buyTokensModal(true) : userStore.loginModal(true)
  }

  const isActive = (route?: string) => {
    if (!route) return false
    if (route === '/') return location.pathname === '/'
    return location.pathname.startsWith(route)
  }

  const handleItemClick = (item: NavItem) => {
    if (item.action === 'directory') {
      userStore.directoryModal(true)
    } else if (item.action === 'buyTokens') {
      loggedIn() ? userStore.buyTokensModal(true) : userStore.loginModal(true)
    } else if (item.route) {
      navigate(item.route)
    }
  }

  return (
    <aside class="cf-sidebar">
      <div class="cf-sb-inner">
        {/* ── Wallet chip — top of sidebar, above nav sections ── */}
        <div class="cf-sb-wallet" onClick={handleWalletClick}>
          {/* Collapsed rail: gold coin tile only */}
          <div class="cf-sb-wallet-coin">
            <div class="cf-sb-wallet-coin-tile">
              <TokenCoin size={22} />
            </div>
          </div>
          {/* Expanded: full chip with coin tile + label + "+" tile */}
          <div class="cf-sb-wallet-expanded">
            {/* Left: coin tile */}
            <div class="cf-sb-wallet-coin-tile">
              <TokenCoin size={22} />
            </div>
            {/* Centre: label */}
            <div class="cf-sb-wallet-info">
              <Show
                when={loggedIn()}
                fallback={<span class="cf-sb-wallet-buy-label">Buy Tokens</span>}
              >
                <span class="cf-sb-wallet-balance">{tokens()}</span>
                <span class="cf-sb-wallet-label">TOKENS</span>
              </Show>
            </div>
            {/* Right: "+" tile — shown always in expanded state */}
            <button
              class="cf-sb-wallet-plus"
              onClick={(e) => {
                e.stopPropagation()
                loggedIn() ? userStore.buyTokensModal(true) : userStore.loginModal(true)
              }}
              title="Buy more tokens"
            >
              +
            </button>
          </div>
        </div>

        <For each={navSections}>
          {(section, sIdx) => (
            <Show when={!section.adminOnly || user().user?.admin}>
              <>
                {sIdx() > 0 && <div class="cf-sb-divider" />}
                <div class="cf-sb-section-label">{section.label}</div>
                <For each={section.items}>
                  {(item) => {
                    // Skip the tokens nav item — promoted to the wallet chip above
                    if (item.id === 'tokens') return null

                    if (item.loggedInOnly && !loggedIn()) {
                      return (
                        <button
                          class="cf-sb-item"
                          onClick={() => userStore.loginModal(true)}
                        >
                          <span class="cf-sb-icon">
                            <item.icon size={16} />
                          </span>
                          <span class="cf-sb-label">{item.label}</span>
                        </button>
                      )
                    }

                    if (item.children) {
                      return <AccountDropdown item={item} collapsed={props.collapsed} />
                    }

                    const active = () =>
                      item.route ? isActive(item.route) : false

                    return (
                      <button
                        class={`cf-sb-item ${active() ? 'active' : ''}`}
                        onClick={() => handleItemClick(item)}
                      >
                        <span class="cf-sb-icon">
                          <item.icon size={16} />
                        </span>
                        <span class="cf-sb-label">{item.label}</span>
                      </button>
                    )
                  }}
                </For>
              </>
            </Show>
          )}
        </For>
      </div>

      {/* Footer with socials */}
      <div class="cf-sb-footer">
        <div class="cf-sb-socials">
          <a class="cf-sb-social" href="https://x.com/cosplayfans__" target="_blank" rel="noopener">
            <IconTwitter size={14} />
          </a>
          <a class="cf-sb-social" href="https://www.instagram.com/cosplayfansofficial/" target="_blank" rel="noopener">
            <IconInstagram size={14} />
          </a>
          <a class="cf-sb-social" href="https://www.facebook.com/cosplayfansofficial/" target="_blank" rel="noopener">
            <IconFacebook size={14} />
          </a>
        </div>
      </div>
    </aside>
  )
}

export function SidebarToggle(props: { collapsed: boolean; onToggle: () => void }) {
  return (
    <button class="cf-sb-toggle" onClick={props.onToggle}>
      <IconChevRight size={10} />
    </button>
  )
}

function AccountDropdown(props: { item: NavItem; collapsed: boolean }) {
  const [open, setOpen] = createSignal(false)
  const location = useLocation()

  const isActive = (route: string) => location.pathname.startsWith(route)
  const anyChildActive = () => props.item.children?.some((c) => isActive(c.route)) ?? false

  return (
    <div class={`cf-sb-dropdown ${open() ? 'open' : ''}`}>
      <button
        class={`cf-sb-item ${anyChildActive() ? 'active' : ''}`}
        onClick={() => setOpen(!open())}
      >
        <span class="cf-sb-icon">
          <props.item.icon size={16} />
        </span>
        <span class="cf-sb-label">{props.item.label}</span>
        <span class="cf-sb-drop-arrow">▾</span>
      </button>

      <div class="cf-sb-drop-menu">
        <For each={props.item.children}>
          {(child) => (
            <A
              href={child.route}
              class={`cf-sb-sub-item ${isActive(child.route) ? 'active' : ''}`}
            >
              <span class="cf-sb-sub-dot" />
              {child.label}
            </A>
          )}
        </For>
      </div>
    </div>
  )
}
